if (!customElements.get('pepam-gift-finder')) {
  customElements.define('pepam-gift-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cache = new Map();
      this.labels = JSON.parse(this.querySelector('[data-finder-labels]').textContent);
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"]');
      this.results = this.querySelector('.pp-finder__results');
      this.grid = this.querySelector('.pp-finder__grid');
      this.pagination = this.querySelector('.pp-finder__pagination');
      this.submit = this.querySelector('[type="submit"]');
      this.form.hidden = false;
      this.scoped = Boolean(this.dataset.collection);
      this.nativeGrid = this.scoped ? document.getElementById('CollectionAjaxContent') : null;
      this.requestId = 0;
      this.form.onsubmit = event => { event.preventDefault(); this.search(); };
      this.form.onchange = () => {
        this.cancel();
        this.results.hidden = true;
        this.status.textContent = this.labels.dirty;
        this.showNative(true);
      };
      this.form.onreset = () => {
        this.cancel();
        this.grid.replaceChildren();
        this.results.hidden = true;
        this.status.textContent = '';
        this.showNative(true);
        this.saveFilters(true);
      };
      this.querySelector('[data-previous]').onclick = () => this.render(this.page - 1, true);
      this.querySelector('[data-next]').onclick = () => this.render(this.page + 1, true);
      if (this.scoped) {
        const params = new URLSearchParams(location.search);
        if (params.get('pp_filter') === '1') {
          ['person','occasion','budget','sort'].forEach(name => {
            const field = this.form.elements.namedItem(name);
            const value = params.get(`pp_${name}`);
            if (field && value && [...field.options].some(option => option.value === value)) field.value = value;
          });
          this.search();
        }
      }
    }
    showNative(visible) {
      const grid = this.scoped ? document.getElementById('CollectionAjaxContent') : null;
      if (grid) grid.hidden = !visible;
    }
    saveFilters(reset = false) {
      if (!this.scoped) return;
      const url = new URL(location.href);
      ['person','occasion','budget','sort'].forEach(name => {
        const value = this.form.elements.namedItem(name)?.value;
        if (!reset && value) url.searchParams.set(`pp_${name}`, value);
        else url.searchParams.delete(`pp_${name}`);
      });
      if (reset) url.searchParams.delete('pp_filter');
      else url.searchParams.set('pp_filter', '1');
      history.replaceState(history.state, '', url);
    }
    cancel() {
      this.requestId++;
      this.controller?.abort();
      this.submit.disabled = false;
      this.results.removeAttribute('aria-busy');
    }
    async loadCollection(handle, signal) {
      const nativeFilters = [...new URLSearchParams(location.search)].filter(([key]) => key.startsWith('filter.'));
      const cacheKey = handle + (this.scoped ? JSON.stringify(nativeFilters) : '');
      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
      const getPage = async page => {
        const url = new URL(`${this.dataset.root}collections/${encodeURIComponent(handle)}`, location.origin);
        if (this.scoped) {
          for (const [key,value] of new URLSearchParams(location.search)) {
            if (key.startsWith('filter.')) url.searchParams.append(key,value);
          }
        }
        url.searchParams.set('view', 'pepam-finder-data');
        url.searchParams.set('sort_by', 'created-descending');
        url.searchParams.set('page', String(page));
        const response = await fetch(url, {signal, credentials:'same-origin'});
        if (!response.ok) throw new Error('Catalog response failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = doc.querySelector('[data-pp-finder-data]');
        if (!data) throw new Error('Catalog data missing');
        const result = JSON.parse(data.textContent);
        if (!Array.isArray(result.products) || result.page !== page) throw new Error('Invalid catalog page');
        return result;
      };
      const first = await getPage(1);
      let products = first.products;
      for (let page = 2; page <= first.pages; page++) {
        const next = await getPage(page);
        products = products.concat(next.products);
      }
      products = [...new Map(products.map(product => [product.id, product])).values()];
      this.cache.set(cacheKey, products);
      return products;
    }
    async search() {
      this.cancel();
      const request = this.requestId;
      this.controller = new AbortController();
      const controller = this.controller;
      const timer = setTimeout(() => controller.abort(), 30000);
      const values = new FormData(this.form);
      const person = values.get('person');
      const occasion = values.get('occasion');
      const budget = values.get('budget');
      this.submit.disabled = true;
      this.results.hidden = true;
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.labels.loading;
      try {
        const products = await this.loadCollection(this.dataset.collection || person || occasion || 'all', controller.signal);
        if (request !== this.requestId) return;
        this.matches = products.filter(product => product.available
          && (!person || product.groups.includes(person))
          && (!occasion || product.groups.includes(occasion))
          && (!budget || (budget === 'over5000' ? product.price > 5000 : product.price <= Number(budget))));
        const sort = values.get('sort');
        if (sort === 'price_asc') this.matches.sort((a,b) => a.price - b.price);
        if (sort === 'price_desc') this.matches.sort((a,b) => b.price - a.price);
        if (sort === 'title') this.matches.sort((a,b) => a.title.localeCompare(b.title, document.documentElement.lang));
        this.showNative(false);
        this.saveFilters();
        this.render(1, false);
      } catch (error) {
        if (request === this.requestId) { this.status.textContent = this.labels.error; this.showNative(true); }
      } finally {
        clearTimeout(timer);
        if (request === this.requestId) {
          this.submit.disabled = false;
          this.results.removeAttribute('aria-busy');
        }
      }
    }
    render(page, focus) {
      const total = Math.max(1, Math.ceil(this.matches.length / 24));
      this.page = Math.max(1, Math.min(page, total));
      this.grid.replaceChildren();
      this.status.textContent = this.matches.length ? `${this.matches.length} ${this.labels.count}` : this.labels.empty;
      const fragment = document.createDocumentFragment();
      this.matches.slice((this.page - 1) * 24, this.page * 24).forEach(product => {
        const card = document.createElement('a');
        card.className = 'pp-finder__card pp-card-polished';
        card.href = product.url;
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.title;
          image.width = 520;
          image.height = 520;
          image.loading = 'lazy';
          card.append(image);
        } else {
          const placeholder = document.createElement('div');
          placeholder.className = 'pp-card-image-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          card.append(placeholder);
        }
        const title = document.createElement('h3');
        title.className = 'pp-card-title';
        title.title = product.title;
        title.textContent = product.title;
        const price = document.createElement('p');
        price.textContent = `${product.varies ? this.labels.from + ' ' : ''}${product.priceText}`;
        card.append(title, price);
        fragment.append(card);
      });
      this.grid.append(fragment);
      this.results.hidden = !this.matches.length;
      this.pagination.hidden = total <= 1;
      this.querySelector('[data-previous]').disabled = this.page === 1;
      this.querySelector('[data-next]').disabled = this.page === total;
      this.querySelector('[data-page]').textContent = `${this.labels.page} ${this.page} ${this.labels.of} ${total}`;
      if (focus) {
        this.status.focus({preventScroll:true});
        this.status.scrollIntoView({block:'start', behavior:'instant'});
      }
    }
    disconnectedCallback() { this.cancel(); this.ready = false; }
  });
}


